/**
 * AcceptInvitePage — new team member redeems an invite token: shows who they
 * are (name/role/company from the token) and lets them set a password.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Shield, CheckCircle2 } from 'lucide-react';
import { api } from '../utils/api';
import { friendlyQuotaError } from '../utils/quotaError';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'done' | 'error'>('loading');
  const [preview, setPreview] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setState('error'); setError('No invite token in this link.'); return; }
    api.post('/auth/accept-invite/preview', { token })
      .then((res: any) => { setPreview(res.data ?? res); setState('ready'); })
      .catch((e: any) => { setState('error'); setError(e?.response?.data?.message || 'This invite link is invalid or has expired.'); });
  }, [token]);

  const accept = async () => {
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    try {
      await api.post('/auth/accept-invite', { token, password });
      setState('done');
    } catch (e: any) {
      // LUNA-DELTA-001: seat-quota 402s render the friendly why+how message.
      setError(friendlyQuotaError(e, 'Could not accept this invite.'));
    }
  };

  if (state === 'done') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-8 max-w-md w-full text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">You're all set!</h1>
          <p className="text-gray-600 text-sm mb-6">Your account is active. Log in with your email and the password you just created.</p>
          <Link to="/login" className="inline-block px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Go to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow border border-gray-100 p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.svg" alt="Conformara logo" className="h-9 w-9" />
          <h1 className="text-xl font-bold text-gray-900">Join your team</h1>
        </div>

        {state === 'loading' && <p className="text-sm text-gray-500">Checking your invite…</p>}

        {state === 'error' && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {state === 'ready' && preview && (
          <>
            <p className="text-sm text-gray-600 mb-1">You've been invited as</p>
            <p className="font-semibold text-gray-900 mb-1">{preview.firstName} {preview.lastName}</p>
            <p className="text-sm text-gray-500 mb-6">{preview.role} · {preview.companyName}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Create a password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="8+ characters" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && accept()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            {error && <div className="mt-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
            <button onClick={accept}
              className="mt-5 w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
              Activate my account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
